import React from "react";

/**
 * Renders a product image with WebP source + PNG fallback via <picture>.
 *
 * Every product's image URL points at a local /products/{slug}.png with a
 * cache-bust query (?v={md5-8chars}). The matching /products/{slug}.webp
 * ships alongside — guaranteed by the backend's image-migration on boot
 * (external CDN URLs get rewritten to local paths) + the seed IMG map
 * pointing exclusively at local files. That means the <source> below is
 * safe: the WebP file is always on disk, browsers never 404 and fall back.
 *
 * If somehow a product still has an external http(s) image URL (e.g. an
 * admin uploaded a photo to an external host on purpose), we detect that
 * and skip the <source> entirely — plain <img> serves whatever URL is
 * there. Never breaks a photo again.
 *
 * WebP variants average ~60 KB vs ~1 MB PNG — 95% file-weight reduction,
 * a large Core Web Vitals / LCP win on mobile.
 *
 * Alt text is form-aware — nasal sprays get nasal-spray phrasing, lab
 * supplies get diluent phrasing, everything else defaults to peptide vial.
 * Googlebot reads alt text as an on-page keyword signal.
 */
export default function ProductImage({ product, className = "", style = {}, alt, size }) {
  const inferredSize = size || product?.variants?.[0]?.size || "";
  const cat = (product?.category || "").toLowerCase();
  const slug = (product?.slug || "").toLowerCase();
  const isNasalSpray = cat.includes("prosource mist") || slug.includes("nasal-spray") || slug === "promist-bundle";
  const isSupply = cat.includes("lab supplies");
  const isCosmetic = cat.includes("skincare") || cat.includes("cosmeceutical") || slug === "blue-renewal";

  let descriptor;
  if (isCosmetic) descriptor = "multi-peptide facial serum";
  else if (isNasalSpray) descriptor = "ready-to-use research nasal spray bottle";
  else if (isSupply) descriptor = "sterile laboratory diluent vial";
  else descriptor = "lyophilized research vial";

  const seoAlt =
    alt ||
    (product?.name
      ? `${product.name}${inferredSize ? ` ${inferredSize}` : ""} ${descriptor} — ProSource Labs`
      : "Research peptide product");

  const src = product?.image || "";
  // Only offer a WebP alternate when the src is a local /products/*.png URL.
  // External URLs (e.g. jpg on a third-party CDN) don't have known WebP
  // variants, so we skip the <source> to avoid the "404'd source never
  // falls back to <img>" browser behaviour.
  const isLocalPng = /^\/products\/[^/]+\.png(\?|$)/i.test(src);
  const webpSrc = isLocalPng ? src.replace(/\.png(\?|$)/i, ".webp$1") : "";

  return (
    // The className / style props go on the <picture> wrapper so it
    // participates in the parent's flex/grid sizing (previous version
    // applied them to the inner <img>, which broke centering and sizing
    // when we switched from a raw <img> to <picture>+<source>). The inner
    // <img> now fills the <picture> and preserves aspect via object-contain.
    <picture className={className} style={style}>
      {webpSrc && <source srcSet={webpSrc} type="image/webp" />}
      <img
        src={src}
        alt={seoAlt}
        data-testid={`product-image-${product?.slug || "x"}`}
        loading="lazy"
        decoding="async"
        style={{
          width: "100%",
          height: "100%",
          // Cosmetic products (landscape composition like Blue Renewal): fill
          // the entire container by cropping the dark background around the
          // product photo. Actual product (box + bottle) stays fully visible
          // because it sits centered in the photo with dark space to crop.
          // Everything else keeps `contain` (peptide vials fit as-is).
          objectFit: isCosmetic ? "cover" : "contain",
          objectPosition: "center",
          display: "block",
        }}
      />
    </picture>
  );
}
